import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useUpdateOrganizationContact } from '~/api/organizations';
import { Button } from '~/components/ui/buttons/Button';
import { useFormData } from '~/components/ui/forms/useForm';
import { PhoneNumberInput } from '~/components/ui/inputs/PhoneNumberInput';
import { optionalPhoneNumberSchema } from '~/components/ui/inputs/phoneNumber';
import { SectionPanel } from '~/components/ui/SectionPanel';
import type { GetOrganizationQuery } from '~/graphql/graphql';
import { extractGraphQLErrorMessages, type ClientError } from '~/utils';

const contactNumbersValidation = z.object({
  phoneNumber: optionalPhoneNumberSchema,
  faxNumber: optionalPhoneNumberSchema,
});

type OrganizationContact = Pick<
  NonNullable<GetOrganizationQuery['organization']>,
  'id' | 'phoneNumber' | 'faxNumber'
>;

type OrganizationContactCardProps = Readonly<{
  organization: OrganizationContact;
}>;

export function OrganizationContactCard({
  organization,
}: OrganizationContactCardProps) {
  return (
    <ContactNumbersForm
      key={`${organization.id}:${organization.phoneNumber}:${organization.faxNumber}`}
      organization={organization}
    />
  );
}

function ContactNumbersForm({ organization }: OrganizationContactCardProps) {
  const { t } = useTranslation('organizations');
  const savedPhoneNumber = organization.phoneNumber ?? null;
  const savedFaxNumber = organization.faxNumber ?? null;
  const form = useFormData({
    initialData: {
      phoneNumber: savedPhoneNumber ?? '',
      faxNumber: savedFaxNumber ?? '',
    },
    validation: contactNumbersValidation,
  });
  const mutation = useUpdateOrganizationContact();

  const parsed = contactNumbersValidation.safeParse(form.data);
  const fieldErrors = parsed.success
    ? {}
    : z.flattenError(parsed.error).fieldErrors;
  const dirty = parsed.success
    ? parsed.data.phoneNumber !== savedPhoneNumber ||
      parsed.data.faxNumber !== savedFaxNumber
    : form.hasChanges;

  const onSave = () => {
    if (!parsed.success) {
      return;
    }
    mutation.mutate(
      { organizationId: organization.id, input: parsed.data },
      {
        onSuccess: () => toast.success(t('organizations.detail.contact.saved')),
        onError: (error: ClientError) =>
          toast.error(extractGraphQLErrorMessages(error).join(', ')),
      }
    );
  };

  return (
    <SectionPanel title={t('organizations.detail.contact.title')}>
      <div className="flex flex-wrap gap-4">
        <div className="w-full max-w-md">
          <PhoneNumberInput
            label={t('organizations.detail.contact.phoneNumber')}
            extensionLabel={t(
              'organizations.detail.contact.phoneNumberExtension'
            )}
            name="phoneNumber"
            value={form.data.phoneNumber as string}
            onChange={form.onChange}
            error={
              fieldErrors.phoneNumber
                ? t('organizations.detail.contact.phoneNumberInvalid')
                : undefined
            }
            disabled={mutation.isPending}
            dataCy="org-contact-phone"
          />
        </div>
        <div className="w-full max-w-md">
          <PhoneNumberInput
            label={t('organizations.detail.contact.faxNumber')}
            extensionLabel={t(
              'organizations.detail.contact.phoneNumberExtension'
            )}
            name="faxNumber"
            value={form.data.faxNumber as string}
            onChange={form.onChange}
            error={
              fieldErrors.faxNumber
                ? t('organizations.detail.contact.faxNumberInvalid')
                : undefined
            }
            disabled={mutation.isPending}
            dataCy="org-contact-fax"
          />
        </div>
      </div>
      <div className="mt-4 flex justify-start gap-2">
        <Button
          variant="secondary"
          size="sm"
          width="auto"
          onClick={form.clear}
          disabled={!dirty || mutation.isPending}
          data-testid="org-contact-cancel"
        >
          {t('organizations.detail.contact.cancel')}
        </Button>
        <Button
          size="sm"
          width="auto"
          onClick={onSave}
          disabled={!dirty || !parsed.success}
          loading={mutation.isPending}
          data-testid="org-contact-save"
        >
          {t('organizations.detail.contact.save')}
        </Button>
      </div>
    </SectionPanel>
  );
}
